// src/Config.js

const isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);
const isProduction = process.env.NODE_ENV === "production" && !isLocal;

const Config = {
  // API Configuration
  API: {
    SITE_BASE_URL: isProduction
      ? process.env.REACT_APP_SITE_BASE_URL_PRODUCTION
      : process.env.REACT_APP_SITE_BASE_URL_DEVELOPMENT,

    API_BASE_URL: isProduction
      ? process.env.REACT_APP_API_BASE_URL_PRODUCTION
      : process.env.REACT_APP_API_BASE_URL_DEVELOPMENT,

    WEBSITE_BASE_URL: isProduction
      ? process.env.REACT_APP_VIEW_WEBSITE_PRODUCTION
      : process.env.REACT_APP_VIEW_WEBSITE_DEVELOPMENT,

    GOOGLE_API_KEY: process.env.REACT_APP_GOOGLE_API_KEY,
    TIME_ZONE_DB_KEY: process.env.REACT_APP_TIME_ZONE_DB_KEY,
  },

  // App Settings
  APP: {
    URL: process.env.REACT_APP_URL,
    NAME: process.env.REACT_APP_NAME,
    VERSION: process.env.REACT_APP_VERSION,
    DESCRIPTION: process.env.REACT_APP_DESCRIPTION,
    IDLE_TIMEOUT_MINUTES: process.env.REACT_APP_IDLE_TIMEOUT_MINUTES,
    MAX_PHOTO_UPLOADS: process.env.REACT_APP_MAX_PHOTO_UPLOADS,
  },

  USERS: {
    DEFAULT_USER_LEVEL: process.env.REACT_APP_DEFAULT_USER_LEVEL,
    ADMIN_USER_LEVEL: process.env.REACT_APP_ADMIN_USER_LEVEL,
    SUPER_ADMIN_USER_LEVEL: process.env.REACT_APP_SUPER_ADMIN_USER_LEVEL,
  },

  CONST: {
    PLACEHOLDER:
      "data:image/svg+xml;utf8," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 675" preserveAspectRatio="xMidYMid slice">
      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#12171d"/><stop offset="100%" stop-color="#0a0d11"/></linearGradient></defs>
      <rect width="1200" height="675" fill="url(#g)"/>
      <g fill="none" stroke="#2e3a46" stroke-width="2" opacity=".35">
        <path d="M0 540 L1200 270 M0 420 L1200 150 M0 300 L1200 30"/>
      </g>
      <g font-family="Inter,Segoe UI,Arial" text-anchor="middle" fill="#8fbfff">
        <text x="600" y="340" font-size="64" font-weight="800">FFLS.io</text>
        <text x="600" y="402" font-size="34" fill="#b9c9d8" opacity=".9">News &amp; Guides</text>
      </g>
    </svg>`
      )
  },

  // End Points
  ENDPOINTS: {
    SEARCH_DEALERS: "/api/dealers/search",
    DEALERS_DETAILS: "/api/dealers",
    DEALERS_FFL_LOCATIONS: "/api/dealers/fflLocations",
    DEALERS_ZIPCODE: "/api/dealers/zipcode",
    DEALERS_STATES: "/api/dealers/states",
    DEALERS_TYPES: "/api/dealers/types",
    DEALERS_SEARCH_RADIUS: "/api/dealers/searchRadius",
    DEALERS_PENDING_FFL_CLAIMS: "/api/dealers/pending-ffl-claims",
    DEALERS_FETCH_PENDING_FFL_CLAIM: "/api/dealers/fetch-pending-ffl-claim",
    DEALERS_GET_PENDING_FFL_CLAIM: "/api/dealers/get-pending-ffl-claim",
    DEALERS_APPROVE_FFL_CLAIM: "/api/dealers/approve-ffl-claim",
    DEALERS_MY_FFLS: "/api/dealers/my-ffls",
    DEALERS_ALL_FFL_CLAIMS_BY_STATUS: "/api/dealers/all-ffl-claims-by-status",
    DEALERS_MY_FFLS_BY_STATUS: "/api/dealers/my-ffls-by-status",
    DEALERS_CHECK_IF_ANY_PENDING: "/api/dealers/check-if-any-pending",
    DEALERS_CLAIM_FFL: "/api/dealers/claim-ffl",
    DEALERS_GET_FFL_EXPIRATION: "/api/dealers/get-ffl-expiration",
    DEALERS_UPLOAD_RENEWAL: "/api/dealers/upload-renewal",
    DEALERS_DOWNLOAD_FFL: "/api/dealers/download-ffl",
    DEALERS_GET_SUB: "/api/dealers/subdomain",
    DEALERS_CHECK_SUBDOMAIN: "/api/dealers/check-subdomain",
    DEALERS_GET_TEMPLATE: "/api/dealers/get-template",
    DEALERS_FETCH_ACTIVE_TEMPLATE: "/api/dealers/fetch-active-templates",
    DEALERS_TOGGLE_SITE_STATUS: "/api/dealers/toggle-site-status",
    DEALERS_SEARCH: '/api/dealers/search',

    SAVED_DEALERS: {
      BASE: "/api/saved-dealers",
      LIST: "/api/saved-dealers", // same as BASE, but explicit
      STATUS: (id) => `/api/saved-dealers/${id}/status`,
      UNSAVE: (id) => `/api/saved-dealers/${id}`,
    },

    PHOTO_DELETE: "/api/dealers/delete-photo",

    USER_INFO: "/api/auth/me",
    USER_RESET_PASSWORD: "/api/auth/reset-password",
    USER_UPDATE_PASSWORD: "/api/auth/update-password",
    USER_UPDATE_EMAIL: "/api/auth/update-email",
    USER_TIMEZONES: "/api/timezones/timezones",
    USER_LOGIN: "/api/auth/login",
    USER_LOGOUT: "/api/auth/user-logout",
    USER_CHECK_LOGIN: "/api/auth/check-login",
    USER_TRIGGER_MFA: "/api/auth/trigger-mfa",
    USER_VERIFY_MFA: "/api/auth/verify-mfa",
    USER_GET_LOGIN_ACTIVITY: "/api/auth/login-activity",
    USER_GET_LAST_LOGIN: "/api/auth/last-login",
    USER_REGISTER: "/api/auth/register",
    USER_PASSWORD_LINK: "/api/auth/send-password-reset-link",
    USER_COMPLETE_REGISTRATION: "/api/auth/complete-registration",
    USER_VERIFY_CODE: "/api/auth/verify-code",
    USER_FETCH_VERIFICATION_DETAILS: "/api/auth/fetch-verification-details",
    USER_CODE_STATUS: "/api/auth/code-status",
    USER_UPDATE_ACCOUNT: "/api/auth/user-update-account",
    USER_SECURITY_META: "/api/auth/security-meta",
    USERS_GET_ALL: "/api/auth/get-all-users",
    USERS_IS_ADMIN: "/api/auth/admin-only",
    USERS_GET_DETAILS: "/api/auth/user-get-details",
    USER_TOGGLE_ACTIVE: "/api/auth/user-toggle-active",
    USER_TOGGLE_MFA: "/api/auth/user-toggle-mfa",
    USER_MFA_ENABLE_COMPLETE: "/api/auth/user-mfa-enable",
    USER_MFA_DISABLE_COMPLETE: "/api/auth/user-mfa-disable",
    USER_MFA_RESEND_ENABLE: "/api/auth/user-mfa-resend-enable",
    USER_MFA_RESEND_DISABLE: "/api/auth/user-mfa-resend-disable",
    USER_LOGIN_MFA_VERIFY: "/api/auth/login-mfa-verify",
    USER_LOGIN_MFA_RESEND: "/api/auth/login-mfa-resend",
    USER_REGISTER_MFA_VERIFY: "/api/auth/register-mfa-verify",
    USER_REGISTER_MFA_RESEND: "/api/auth/register-mfa-resend",
    USER_UPDATE_EMAIL_INIT: "/api/auth/user-update-email-init",
    USER_UPDATE_EMAIL_VERIFY: "/api/auth/user-update-email-verify",
    USER_UPDATE_EMAIL_RESEND: "/api/auth/user-update-email-resend",
    USER_SEND_EMAIL: "/api/email/send-email",

    ADMIN_CLAIM_START: "/api/admin/start",
    ADMIN_CLAIM_COMPLETE: "/api/admin/complete",
    ADMIN_CLAIM_RESEND: "/api/admin/resend",

    ADMIN_ACTION_START: "/api/admin/action-start",
    ADMIN_ACTION_COMPLETE: "/api/admin/action-complete",
    ADMIN_ACTION_RESEND: "/api/admin/action-resend",

    AI_GENERATE_DESCRIPTION: "/api/ai/generate-description",

    // --- Blog / News & Guides ---
    BLOG_LIST: "/api/blog",                  // GET ?page=&limit=
    BLOG_BY_SLUG: "/api/blog/slug",          // GET /api/blog/slug/:slug
    DEALER_POSTS: "/api/blog/dealer",        // GET /api/blog/dealer/:fflNumber?limit=3
  },
};

export default Config;